import React, { useEffect, useMemo, useState, useRef } from "react";
import { OrderItem, useOrder, mergeCartIntoOrder } from "../../context/OrderContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { AuthModal } from "../../components/SignupSigninModal";
import DeliveryAddressSection from "../../components/DeliveryAddressSection";
import AccountSection from "../../components/AccountSection";
import PaymentSection from "../../components/PaymentSection";
import BillDetailsCard from "../../components/BillDetailCard";
import { useDelivery } from "../../context/DeliveryContext";
import { getAsapScheduledTime, saveAsapDelay, loadAsapDelay } from "../../lib/utils";
import { MoveLeft, Clock } from "lucide-react";
import {
  getSignupLinkData,
  storePendingSignupCode,
  clearPendingSignupCode,
  clearSignupParamsFromUrl,
} from "../../utils/signupLink";
// Types
import { useSocket } from "../../context/SocketContext";
import {
  createDoorDashOrder,
  createOrder,
  genrateOrderQuote,
  getHotelInformation,
  verifyProfileComplete,
} from "../../services/apis";
import CartItemCard from "./CardItem";

import toast from "react-hot-toast";
import api, { ApiResponse } from "../../services";
import Cookies from "js-cookie";
import Tipmodal from "../../components/Tipmodal";
import Yourinformation, {
  YourinformationRef,
} from "../../components/ui/Yourinformation";
import { UsetempUser } from "../../context/TempuserContext";
interface CartItem {
  id: string;
  name: string;
  restaurant: string;
  price: number;
  quantity: number;
  isVeg: boolean;
  customizations?: string[];
  image?: string;
}

interface BillDetails {
  itemTotal: number;
  deliveryFee: number;
  tipAmount?: number;
  toPay: number;
}

interface Address {
  _id?: string;
  type: "Home" | "Office" | "Others";
  tag?: string;
  FlatBlock?: string;
  Area?: string;
  latitude?: number;
  longitude?: number;
}

interface HotelData {
  hotelName: string;
  address: string;
  latitude: number;
  longitude: number;
  phoneNo: string;
  deliveryTipToPartner?: boolean;
  baseTipPercent?: number;
  tipGapPercent?: number;
  withinRadius?: number;
  minOrderAmount?: number;
  deliveryAmount?: number;
}

interface Taxes {
  category_code?: string;
  // US
  tax_percent?: number;
  // Canada
  gst?: number;
  pst?: number;
  id?: string;
}

function getTaxRate(t: Taxes): number {
  if (t.gst != null || t.pst != null) return (t.gst ?? 0) + (t.pst ?? 0);
  return t.tax_percent ?? 0;
}

function resolveTaxConfig(item: any, taxPercentage: Taxes[]): Taxes | undefined {
  if (!taxPercentage?.length) return undefined;
  const specific = taxPercentage.find((t) => t.id && t.id === item.categoryId);
  if (specific) return specific;
  const byCategoryCode = taxPercentage.find(
    (t) => t.category_code && t.category_code === item.categoryId,
  );
  if (byCategoryCode) return byCategoryCode;
  return taxPercentage.find((t) => t.category_code === "default") ?? taxPercentage[0];
}

function resolveTaxRate(item: any, taxPercentage: Taxes[]): number {
  const taxConfig = resolveTaxConfig(item, taxPercentage);
  if (!taxConfig) return 0;
  return getTaxRate(taxConfig);
}

// Main Checkout Page Component
export const CheckoutPage: React.FC = () => {
  const { orderData, setOrderData, resetOrderContext, totalDealDiscount } =
    useOrder();
  const [totalTime, setTotalTime] = useState(0);
  const { user, setUser, currencySymbol, refreshBalances } = useAuth();
  // Balances change server-side (refunds, loyalty); show current values
  useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);
  const { tempUser, settempUser } = UsetempUser();
  const yourinformationRef = useRef<YourinformationRef>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [uberDeliveryId, setUberDeliveryId] = useState<string | null>(null);
  const [doordashDeliveryId, setDoordashDeliveryId] = useState<string | null>(
    null,
  );
  const [uberDeliveryFee, setUberDeliveryFee] = useState(0);
  const [hotelData, setHotelData] = useState<HotelData | null>(null);
  const [uberDeliverable, setUberDeliverable] = useState<boolean>(false);
  const {
    orderType,
    delivery,
    pickup: pickupData,
    taxPercentage,
    updateTaxesForRestaurant,
  } = useDelivery();

  // Get the restaurant location - for pickup it's the pickup location, for delivery it's the restaurantLocation
  const currentRestaurant =
    orderType === "pickup"
      ? pickupData.locationObject
      : delivery.restaurantLocation;
  const [deliverable, setDeliverable] = useState<boolean>(false);
  const [deliveryStatusLoading, setDeliveryStatusLoading] =
    useState<boolean>(false);
  const [doordashDeliveryFee, setDoordashDeliveryFee] = useState(0);
  const [doordashDeliverable, setDoordashDeliverable] =
    useState<boolean>(false);
  const [doordashDeliverTime, setDoordashDeliverTime] = useState<number>(0);
  const [baseDeliveryFee, setBaseDeliveryFee] = useState<number>(0);
  const [selectedPlatform, setSelectedPlatform] = useState<
    "uber" | "doordash" | "custom" | null
  >("uber");
  const { socket } = useSocket();
  const [deliveryTaxes, setDeliveryTaxes] = useState<number>(0);
  const [tipAmount, setTipAmount] = useState<number>(0);
  const lastQuoteRequestRef = useRef<string>("");
  function formatAddress(addressString: string) {
    const parts = addressString.split(",").map((p) => p.trim());

    if (parts.length < 4) {
      throw new Error("Address format not recognized");
    }

    const [street, city, state, country] = parts;

    return JSON.stringify({
      street_address: [street],
      city,
      state,
      zip_code: "", // ZIP not present in this case
      country: country === "USA" ? "US" : country,
    });
  }
  useEffect(() => {
    console.log("Current Restaurant Data:", currentRestaurant);
  }, [currentRestaurant]);

  const calculatePrepTime = (items: any[]) => {
    if (!items || items.length === 0) return 0;
    console.log("Calculating prep time for items:", items);
    return Math.max(...items.map((item) => item.prepTime || 0));
  };

  // ASAP orders: estimated prep = restaurant's current delay + order prep time.
  // After a reload the delivery context is empty, so fall back to the delay
  // cached for this restaurant (or the quote's nearest restaurant).
  const asapRestaurantId = currentRestaurant?.id || orderData?.restaurantId;
  useEffect(() => {
    saveAsapDelay(currentRestaurant?.id, currentRestaurant?.asapDelay);
  }, [currentRestaurant?.id, currentRestaurant?.asapDelay]);
  const asapDelay =
    currentRestaurant?.asapDelay ??
    loadAsapDelay(asapRestaurantId) ??
    (orderType === "delivery" ? (hotelData as any)?.asapDelay : undefined) ??
    0;
  const asapPrepEstimate =
    asapDelay + calculatePrepTime(orderData?.items || []);

  // Example usage:
  const pickup = "201 Waterfront Street, National Harbor, Maryland, USA";
  console.log(formatAddress(pickup));

  const generateOrderQuote = async (
    dropoff_address: string,
    dropoff_latitude: number,
    dropoff_longitude: number,
  ) => {
    try {
      const prepTime = calculatePrepTime(orderData?.items || []);
      setDeliveryStatusLoading(true);
      const res = await genrateOrderQuote({
        dropoff_address,
        dropoff_latitude,
        dropoff_longitude,
        pickup_time: orderData?.scheduledAt
          ? new Date(
              new Date(orderData.scheduledAt).getTime() + prepTime * 60000,
            ).toISOString()
          : new Date().toISOString(),
      });
      if (res.data.success) {
        setDeliveryStatusLoading(false);
        setHotelData(res.data.nearestRestaurant);
        console.log("Nearest Restaurant ID:", res.data.nearestRestaurant.id);
        setOrderData((prev) =>
          prev
            ? {
                ...prev,
                restaurantId: prev.restaurantId || res.data.nearestRestaurant.id,
              }
            : prev,
        );
        // updateTaxesForRestaurant(res.data.nearestRestaurant.id, res.data.nearestRestaurant.taxes);
        setDeliveryTaxes(res.data.quotes.deliveryTax || 0);
        if (res.data.quotes.uber.success) {
          // --- UBER ---
          setTotalTime(
            res.data.quotes.uber.data.duration -
              res.data.quotes.uber.data.pickup_duration,
          );
          setUberDeliveryId(res.data.quotes.uber.data.id);
          setUberDeliveryFee(res.data.quotes.uber.data.fee / 100);
          setUberDeliveryFee(res.data.quotes.uber.data.fee / 100);
          setUberDeliverable(true);
        }

        // --- DOORDASH ---
        if (res.data.quotes.doordash.success) {
          setDoordashDeliveryFee(res.data.quotes.doordash.data.fee / 100);
          setDoordashDeliverable(true);
          setDoordashDeliveryId(
            res.data.quotes.doordash.data.external_delivery_id,
          );

          const now = Date.now();
          const pickup = new Date(
            res.data.quotes.doordash.data.pickup_time_estimated,
          ).getTime();
          const dropoff = new Date(
            res.data.quotes.doordash.data.dropoff_time_estimated,
          ).getTime();

          // time from current moment → pickup
          const timeToPickup = Math.max(0, Math.round((pickup - now) / 60000));

          // time from pickup → dropoff
          const deliveryTime = Math.round((dropoff - pickup) / 60000);

          // total DoorDash time (same format as Uber)
          const totalDoorDashMinutes = deliveryTime;

          setDoordashDeliverTime(totalDoorDashMinutes);
        } else {
          setDoordashDeliverable(false);
        }

        return res.data.quotes.uber;
      }

      setDeliveryStatusLoading(false);

      return null;
    } catch (err: any) {
      setDeliveryStatusLoading(false);
      setUberDeliverable(false);
      setDoordashDeliverable(false);
      setDeliveryFee(0);

      console.log("Error generating order quote");
      toast.error(`The specified location is not in a deliverable area.`);
    }
  };

  const getHotelData = async () => {
    const res = await getHotelInformation();
    if (res.status === 200) {
      console.log(`ddd`, res.data.data[1]);
      setHotelData(res.data.data[0]);
    } else {
      console.log(res.data.error.message);
      toast.error(res.data.error.message);
    }
  };

  const handleResetDelivery = () => {
    setDeliveryFee(0);
    setBaseDeliveryFee(0);
    setUberDeliveryFee(0);
    setDoordashDeliveryFee(0);
    setDeliverable(false);
    setUberDeliverable(false);
    setDoordashDeliverable(false);
    setSelectedPlatform(null);
    setTotalTime(0);
    setDoordashDeliverTime(0);
  };

  // useEffect(() => {
  //   getHotelData();
  // }, []);

  // Replace the existing tax calculation useEffect with this:
  useEffect(() => {
    if (!taxPercentage || !orderData?.items) return;

    // Create new items array with updated taxes
    const updatedItems = orderData.items.map((item) => {
      const taxConfig = resolveTaxConfig(item, taxPercentage);
      const taxRate = taxConfig ? getTaxRate(taxConfig) : 0;
      const taxAmount = (item.price * taxRate) / 100;
      const gstAmount =
        taxConfig?.gst != null ? (item.price * taxConfig.gst) / 100 : undefined;
      const pstAmount =
        taxConfig?.pst != null ? (item.price * taxConfig.pst) / 100 : undefined;
      return {
        ...item,
        tax: parseFloat(taxAmount.toFixed(2)),
        gst: gstAmount != null ? parseFloat(gstAmount.toFixed(2)) : undefined,
        pst: pstAmount != null ? parseFloat(pstAmount.toFixed(2)) : undefined,
      };
    });

    // Only update if taxes actually changed
    const taxesChanged = updatedItems.some(
      (item, index) =>
        item.tax !== orderData.items?.[index]?.tax ||
        item.gst !== orderData.items?.[index]?.gst ||
        item.pst !== orderData.items?.[index]?.pst,
    );

    if (taxesChanged) {
      setOrderData((prev) =>
        prev
          ? {
              ...prev,
              items: updatedItems,
            }
          : prev,
      );
    }
  }, [taxPercentage]); // stamps tax onto items for API submission when tax rates change

  const { gstTotal, pstTotal } = useMemo(() => {
    if (!orderData?.items?.length || !taxPercentage?.length) {
      return { gstTotal: 0, pstTotal: 0 };
    }
    const totals = orderData.items.reduce(
      (acc, item) => {
        const taxConfig = resolveTaxConfig(item, taxPercentage);
        if (!taxConfig) return acc;
        const qty = Number(item.quantity) || 0;
        if (taxConfig.gst != null) {
          acc.gst += ((item.price * taxConfig.gst) / 100) * qty;
        }
        if (taxConfig.pst != null) {
          acc.pst += ((item.price * taxConfig.pst) / 100) * qty;
        }
        return acc;
      },
      { gst: 0, pst: 0 },
    );
    return {
      gstTotal: Number(totals.gst.toFixed(2)),
      pstTotal: Number(totals.pst.toFixed(2)),
    };
  }, [orderData?.items, taxPercentage]);

  const hasCanadaTax = useMemo(
    () =>
      !!orderData?.items?.some((item) => {
        const taxConfig = resolveTaxConfig(item, taxPercentage);
        return taxConfig?.gst != null || taxConfig?.pst != null;
      }),
    [orderData?.items, taxPercentage],
  );

  // Calculate itemTotal inline using taxPercentage so it's always accurate
  // regardless of when taxPercentage was set (pickup sets it before mount, delivery sets it inside mount)
  const itemTotal = useMemo(() => {
    if (!orderData?.items) return 0;
    return orderData.items.reduce((sum: number, item: any) => {
      const taxRate = resolveTaxRate(item, taxPercentage);
      const tax = (item.price * taxRate) / 100;
      return sum + (item.price + tax) * item.quantity;
    }, 0);
  }, [orderData?.items, taxPercentage]);

  useEffect(() => {
    if (orderType !== "delivery") return;

    const orderLocation = orderData?.location;
    const deliveryContextLocation = delivery.locationObject;

    const quoteAddress = orderLocation?.address || delivery.displayAddress;
    const quoteLat = orderLocation?.lat ?? deliveryContextLocation?.latitude;
    const quoteLng = orderLocation?.lng ?? deliveryContextLocation?.longitude;

    if (!quoteAddress || quoteLat == null || quoteLng == null) return;

    // Keep orderData hydrated when returning from checkout/back navigation.
    if (
      (!orderLocation?.address ||
        orderLocation?.lat == null ||
        orderLocation?.lng == null) &&
      deliveryContextLocation
    ) {
      setOrderData((prev) =>
        prev
          ? {
              ...prev,
              location: {
                address: quoteAddress,
                lat: quoteLat,
                lng: quoteLng,
              },
            }
          : prev,
      );
      // Hydrate first; quote fires on next effect pass.
      return;
    }

    // Include scheduledAt in key so changing scheduled time re-triggers the quote,
    // but the same address+time combo never calls the API twice.
    const requestKey = `${quoteAddress}|${quoteLat}|${quoteLng}|${orderData?.scheduledAt ?? "asap"}`;
    if (lastQuoteRequestRef.current === requestKey) return;
    lastQuoteRequestRef.current = requestKey;

    generateOrderQuote(quoteAddress, quoteLat, quoteLng);
  }, [
    orderType,
    orderData?.location?.address,
    orderData?.location?.lat,
    orderData?.location?.lng,
    delivery.displayAddress,
    delivery.locationObject?.latitude,
    delivery.locationObject?.longitude,
    orderData?.scheduledAt,
    orderData?.scheduledTimezone,
    orderData?.isAsap,
  ]);

  useEffect(() => {
    console.log(
      "Order data changed, checking if we need to regenerate quote...",
      orderData?.scheduledAt,
      orderData?.isAsap,
      orderData?.scheduledTimezone,
    );
  }, [orderData]);

  useEffect(() => {
    if (orderType !== "delivery") {
      lastQuoteRequestRef.current = "";
    }
    // Reset on unmount so re-entering the page always triggers a fresh quote
    return () => {
      lastQuoteRequestRef.current = "";
    };
  }, [orderType]);

  const [isOdering, setIsOdering] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"login" | "signup">("login");

  const [activeTab, setActiveTab] = useState<"login" | "signup">("login");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lockAuthToSignup, setLockAuthToSignup] = useState(false);
  const navigate = useNavigate();

  // Dark mode state - syncs with POS page theme preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem("pos_theme");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("theme-dark");
    } else {
      root.classList.remove("theme-dark");
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (user?.id) {
      clearPendingSignupCode();
      setLockAuthToSignup(false);
      return;
    }

    const { code, shouldForceSignup } = getSignupLinkData();
    if (!shouldForceSignup) return;

    storePendingSignupCode(code);
    setActiveTab("signup");
    setLockAuthToSignup(true);
    setIsModalOpen(true);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    settempUser((prevUser) => ({
      id: user.id,
      email: user.email || prevUser?.email || "",
      phonenumber: user.phonenumber || prevUser?.phonenumber || "",
      name: user.name || prevUser?.name || "",
    }));
  }, [user?.id, user?.email, user?.phonenumber, user?.name, settempUser]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");

  // Pre-tax item subtotal (without any taxes)
  const itemSubtotalPreTax = useMemo(() => {
    if (!orderData?.items) return 0;
    return orderData.items.reduce((sum: number, item: any) => {
      return sum + item.price * item.quantity;
    }, 0);
  }, [orderData?.items]);

  const bonusWalletBalance = user?.bonusWallet?.balance || 0;

  // User-level discount (user.discount) disabled for now — uncomment to re-enable.
  // const discountValue = user?.discount ? (itemTotal * user.discount) / 100 : 0;
  const discountValue = 0;

  // Always compute how much bonus wallet would cover (for disabled checks in PaymentSection).
  // Discounts are applied first, so bonus only covers what remains — otherwise toPay goes negative.
  const bonusUsed = useMemo(() => {
    const discountedSubtotal = Math.max(
      0,
      itemSubtotalPreTax - discountValue - (totalDealDiscount || 0),
    );
    return Number(Math.min(bonusWalletBalance, discountedSubtotal).toFixed(2));
  }, [bonusWalletBalance, itemSubtotalPreTax, discountValue, totalDealDiscount]);

  // Always compute reduced tax (only on non-bonus-covered portion, item-by-item sequential)
  const reducedItemTax = useMemo(() => {
    if (!orderData?.items || bonusUsed === 0) return itemTotal - itemSubtotalPreTax;
    let remainingBonus = bonusUsed;
    let totalTax = 0;
    for (const item of orderData.items) {
      const taxRate = resolveTaxRate(item, taxPercentage);
      const itemSubtotal = item.price * item.quantity;
      let taxableAmount: number;
      if (remainingBonus >= itemSubtotal) {
        remainingBonus -= itemSubtotal;
        taxableAmount = 0;
      } else {
        taxableAmount = itemSubtotal - remainingBonus;
        remainingBonus = 0;
      }
      totalTax += (taxableAmount * taxRate) / 100;
    }
    return totalTax;
  }, [bonusUsed, orderData?.items, taxPercentage, itemTotal, itemSubtotalPreTax]);

  // Per-item bonus coverage: true = fully covered by bonus (no tax shown)
  const itemBonusCoverage = useMemo(() => {
    if (!orderData?.items) return [] as boolean[];
    const isBonus = (selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") && bonusUsed > 0;
    if (!isBonus) return orderData.items.map(() => false);
    let remainingBonus = bonusUsed;
    return orderData.items.map((item: any) => {
      const itemSubtotal = item.price * item.quantity;
      if (remainingBonus >= itemSubtotal) {
        remainingBonus -= itemSubtotal;
        return true;
      }
      remainingBonus = 0;
      return false;
    });
  }, [bonusUsed, orderData?.items, selectedPaymentMethod]);

  // Effective toPay if any bonus method were selected (used for payment method disabled checks)
  const bonusAdjustedToPay = useMemo(() => {
    if (itemSubtotalPreTax <= 0) return 0;
    // Round to cents: float leftovers (e.g. 17.490000000000002 - 17.49) would
    // otherwise look like a card remainder and send a $0 Square payment.
    const remainder =
      itemSubtotalPreTax - bonusUsed + reducedItemTax - discountValue - (totalDealDiscount || 0) + deliveryFee + tipAmount;
    return Math.max(0, Number(remainder.toFixed(2)));
  }, [itemSubtotalPreTax, bonusUsed, reducedItemTax, discountValue, totalDealDiscount, deliveryFee, tipAmount]);

  const parseDistanceMiles = (distance?: string): number | null => {
    if (!distance) return null;
    const match = distance.match(/(\d+(\.\d+)?)/);
    if (!match) return null;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const calculateStraightLineMiles = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number => {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const earthRadiusMiles = 3958.8;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMiles * c;
  };

  const hasRadiusDeliveryPricing =
    typeof currentRestaurant?.withinRadius === "number" &&
    currentRestaurant.withinRadius > 0 &&
    typeof currentRestaurant?.deliveryAmount === "number" &&
    currentRestaurant.deliveryAmount > 0 &&
    typeof currentRestaurant?.minOrderAmount === "number";

  const effectiveDistanceMiles = useMemo(() => {
    const parsedDistance = parseDistanceMiles(currentRestaurant?.distance);
    if (parsedDistance !== null) return parsedDistance;

    if (
      orderType !== "delivery" ||
      !orderData?.location?.lat ||
      !orderData?.location?.lng ||
      !currentRestaurant?.latitude ||
      !currentRestaurant?.longitude
    ) {
      return null;
    }

    return calculateStraightLineMiles(
      orderData.location.lat,
      orderData.location.lng,
      currentRestaurant.latitude,
      currentRestaurant.longitude,
    );
  }, [
    currentRestaurant?.distance,
    currentRestaurant?.latitude,
    currentRestaurant?.longitude,
    orderData?.location?.lat,
    orderData?.location?.lng,
    orderType,
  ]);

  const withinRadiusForCustomPricing =
    hasRadiusDeliveryPricing &&
    effectiveDistanceMiles !== null &&
    effectiveDistanceMiles <= (currentRestaurant?.withinRadius || 0);

  const minAmountForCustomDelivery = currentRestaurant?.minOrderAmount || 0;
  const amountLeftToUnlockDelivery = Math.max(
    minAmountForCustomDelivery - itemTotal,
    0,
  );

  const isCustomDeliveryUnlocked =
    withinRadiusForCustomPricing && amountLeftToUnlockDelivery <= 0;

  const customRadiusFeeApplicable =
    orderType === "delivery" && isCustomDeliveryUnlocked;

  const deliveryPlatform =
    orderType === "delivery"
      ? isCustomDeliveryUnlocked
        ? "custom"
        : selectedPlatform
      : null;
  // Check if guest user has filled all required fields
  const isGuestInfoComplete = React.useMemo(() => {
    if (user) return true; // Logged in user, no need to check

    const hasName = !!tempUser?.name;
    const hasPhone =
      !!tempUser?.phonenumber && tempUser.phonenumber.length === 10;
    const hasValidEmail =
      !!tempUser?.email && /^\S+@\S+\.\S+$/.test(tempUser.email);

    return hasName && hasPhone && hasValidEmail;
  }, [user, tempUser?.email, tempUser?.phonenumber, tempUser?.name]);

  const toPay = React.useMemo(() => {
    if (itemTotal <= 0) return 0;
    if ((selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") && bonusUsed > 0) {
      // Rounded to cents so float leftovers don't become a phantom amount due
      return Math.max(
        0,
        Number(
          (itemSubtotalPreTax - bonusUsed + reducedItemTax - discountValue - (totalDealDiscount || 0) + deliveryFee + tipAmount).toFixed(2),
        ),
      );
    }
    return (
      itemTotal -
      discountValue -
      (totalDealDiscount || 0) +
      deliveryFee +
      tipAmount
    );
  }, [selectedPaymentMethod, bonusUsed, itemSubtotalPreTax, reducedItemTax, itemTotal, discountValue, deliveryFee, totalDealDiscount, tipAmount]);

  const [billDetails, setBillDetails] = useState<BillDetails>({
    itemTotal: 0,
    deliveryFee: 0,
    toPay: 0,
  });

  useEffect(() => {
    setBillDetails({
      itemTotal: Number(itemTotal.toFixed(2)),
      deliveryFee: Number(deliveryFee.toFixed(2)),
      toPay: Number(toPay.toFixed(2)),
    });
  }, [itemTotal, deliveryFee, toPay]);

  const [estimatedLoyaltyBonus, setEstimatedLoyaltyBonus] = useState<number>(0);
  // Orders paid (partly) with bonus balance earn no loyalty bonus
  const paysWithBonus =
    (selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") && bonusUsed > 0;
  const [spendMoreToEarn, setSpendMoreToEarn] = useState<number | null>(null);

  useEffect(() => {
    const phone = user?.phonenumber;
    if (!phone || toPay <= 0) {
      setEstimatedLoyaltyBonus(0);
      setSpendMoreToEarn(null);
      return;
    }
    const controller = new AbortController();
    api
      .get(`/loyalty/estimate?phoneNumber=${encodeURIComponent(phone)}&amount=${itemSubtotalPreTax.toFixed(2)}`, { signal: controller.signal })
      .then((res: any) => {
        if (res.data?.success) {
          const data = res.data.data;
          setEstimatedLoyaltyBonus(data.estimatedBonus ?? 0);
          setSpendMoreToEarn(data.spendMoreToEarn ?? null);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [user?.phonenumber, itemSubtotalPreTax]);

  const manifestItems =
    orderData?.items?.map((item: OrderItem) => ({
      name: item.name,
      quantity: item.quantity,
      price: Math.round(item.price * 100), // convert to cents if price exists
      size: "medium",
    })) || [];

  const handlePlaceOrder = async (
    payChoice: string,
    paymentMethod: string,
    userId?: string,
    transactionId?: string,
  ) => {
    console.log("🟢 handlePlaceOrder called with:");
    console.log("  - payChoice:", payChoice);
    console.log("  - paymentMethod:", paymentMethod);
    console.log("  - userId:", userId);
    console.log("  - transactionId:", transactionId);
    if (!orderData) return;

    const totalAmount = (
      (selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") && bonusUsed > 0
        ? toPay + bonusUsed  // actual total paid = wallet portion + bonus portion (uses reduced tax, not full tax)
        : toPay
    ).toFixed(2);
    const customerPhoneNumber =
      user?.phonenumber || tempUser?.phonenumber || "";
    const customerCountryCode = user?.countryCode || "+1";
    const customerEmail = user?.email || tempUser?.email || "";
    const customerName = user?.name || tempUser?.name || "";
    const existingOrderUserId = userId || user?.id || tempUser?.id;
    let orderUserId = existingOrderUserId;

    if (!orderUserId && customerPhoneNumber) {
      const cleanedPhone = customerPhoneNumber.replace(/\D/g, "");
      if (cleanedPhone.length === 10) {
        try {
          const response = await verifyProfileComplete(cleanedPhone);
          const matchedUser = response?.data?.user;
          const resolvedUserId = matchedUser?.id || matchedUser?._id;

          if (response?.data?.isProfileComplete && resolvedUserId) {
            orderUserId = resolvedUserId;
            settempUser((prevUser) =>
              prevUser ? { ...prevUser, id: resolvedUserId } : prevUser,
            );
          }
        } catch (error) {
          console.error("Failed to resolve userId from phone number:", error);
        }
      }
    }

    // Calculate ASAP target time right before placing the order.
    // This is only used for delivery-partner dispatch timing (pickup_time below),
    // never sent to our own backend as scheduledAt: the backend infers
    // orderStatus "Scheduled" purely from scheduledAt being present, so ASAP
    // orders must omit it entirely to be recorded as "Received".
    let scheduledAt = orderData.scheduledAt;
    let pickupTargetTime = scheduledAt;
    if (orderData.isAsap) {
      const timezone = orderData.scheduledTimezone || "America/Denver";
      const delayMinutes = asapDelay;
      const asapData = getAsapScheduledTime(timezone, delayMinutes);
      pickupTargetTime = asapData.isoString;
      scheduledAt = undefined;
      console.log(
        "ASAP order - calculated pickup target time at checkout:",
        pickupTargetTime,
      );
    }

    // The restaurant fulfilling this order (pickup location or delivering restaurant).
    // After a reload the delivery context is empty, so fall back to the cart's
    // restaurantId and then to the nearest restaurant from the delivery quote.
    const restaurantId =
      currentRestaurant?.id ||
      orderData.restaurantId ||
      (orderType === "delivery" ? (hotelData as any)?.id : undefined);
    if (!restaurantId) {
      setIsOdering(false);
      toast.error("Please select your restaurant location again before placing the order.");
      return;
    }

    const finalData = {
      ...orderData,
      restaurantId,
      scheduledAt, // Only set for real scheduled orders; omitted for ASAP
      totalAmount,
      payChoice,
      choice: orderType === "delivery" ? "online" : "Takeaway",
      tipAmount: tipAmount.toFixed(2),
      phoneNumber: customerPhoneNumber,
      paymentMethod: paymentMethod,
      userId: orderUserId,
      gstAmount: gstTotal,
      pstAmount: pstTotal,
    };
    setOrderData(finalData);

    try {
      console.log(user);
      let res;
      // Use pickupLocation for pickup orders, hotelData for delivery orders
      const pickupInfo =
        orderType === "pickup" && orderData?.pickupLocation
          ? orderData.pickupLocation
          : hotelData;

      const prepTime = calculatePrepTime(orderData?.items || []);

      const finalOrderData = {
        pickup_name: pickupInfo?.hotelName || "",
        pickup_address: pickupInfo?.address || "",
        tipToDriver: hotelData?.deliveryTipToPartner || false,
        pickup_phone_number: pickupInfo?.phoneNo
          ? "+1" + pickupInfo.phoneNo
          : "",
        dropoff_name: orderData?.location?.address || "",
        dropoff_address: orderData?.location?.address || "",
        dropoff_phone_number: customerPhoneNumber
          ? `${customerCountryCode}${customerPhoneNumber}`
          : "",
        manifest_items: manifestItems,
        platform: deliveryPlatform,
        dropoff_notes: delivery.instructions || "",
        dropoff_latitude:
          orderData?.location?.lat ?? delivery.locationObject?.latitude ?? 0,
        dropoff_longitude:
          orderData?.location?.lng ?? delivery.locationObject?.longitude ?? 0,
        pickup_time: pickupTargetTime
          ? new Date(
              new Date(pickupTargetTime).getTime() + prepTime * 60000,
            ).toISOString()
          : new Date().toISOString(),
      };

      // if (selectedPlatform == "uber") {
      //   res = await createOrder(finalOrderData);
      // } else if (selectedPlatform == "doordash") {
      //   res = await createDoorDashOrder(finalOrderData);
      // }

      // socket?.emit("NEW_ORDER_PLACED", {
      //   message: "New order placed",
      //   deliveryId:
      //     res?.data?.data?.id || res?.data?.data?.external_delivery_id || null,
      // });

      const orderdata = {
        ...finalData,
        transactionId: transactionId || undefined,
        deliveryAmount: deliveryFee,
        deliveryLiveMapUrl: "",
        deliveryId:
          deliveryPlatform === "uber"
            ? uberDeliveryId
            : deliveryPlatform === "doordash"
              ? doordashDeliveryId
              : null,
        platform: deliveryPlatform,
        deliveryTime: totalTime,
        userId: orderUserId,
        email: customerEmail || undefined,
        name: customerName || undefined,
        discountAmount: Number(discountValue.toFixed(2)),
        bonusAmountUsed: (selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") ? Number(bonusUsed.toFixed(2)) : 0,
        gstAmount: gstTotal,
        pstAmount: pstTotal,
        metadata: finalOrderData,
        orderPrepTime: prepTime,
      };

      // Step 2: Place the order

      console.log("user d", orderData);
      console.log("🟡 orderdata being sent to API:", orderdata);
      console.log("🟡 transactionId in orderdata:", orderdata.transactionId);
      const { data } = await api.post<ApiResponse<any>>("/orders", orderdata);

      setIsOdering(false);
      if (data.success) {
        // toast.success("Order placed successfully!");
        if (data.user && data.user.wallet) {
          setUser((prev) =>
            prev && data.user
              ? {
                  ...prev,
                  wallet: data.user.wallet
                    ? {
                        ...prev.wallet,
                        balance: data.user.wallet.balance,
                      }
                    : prev.wallet,

                  bonusWallet: data.user.bonusWallet
                    ? {
                        ...prev.bonusWallet,
                        balance: data.user.bonusWallet.balance,
                      }
                    : prev.bonusWallet,
                }
              : prev,
          );
        }

        resetOrderContext();
        if (!user) {
          Cookies.remove("guest_cart");
        }
        // if (res && res.data.success) navigate(`/confirmation/${data.data._id}`);
        if (data?.success) {
          // Freeze the delay used for this order so the confirmation page's
          // prep estimate stays the same after a reload.
          if (orderData?.isAsap) saveAsapDelay(data.data._id, asapDelay);
          navigate(`/confirmation/${data.data._id}`);
        }
      } else {
        // toast.error(data.error || "Failed to place order");
      }
    } catch (err: any) {
      setIsOdering(false);
      // toast.error(err.response?.data?.error || "Failed to place order");
    }
  };

  useEffect(() => {
    if (orderType !== "delivery") return;
    if (isCustomDeliveryUnlocked) {
      if (selectedPlatform !== "custom") {
        setSelectedPlatform("custom");
      }
      return;
    }

    if (uberDeliverable) {
      if (selectedPlatform === null || selectedPlatform === "custom") {
        setSelectedPlatform("uber");
      }
      return;
    }

    if (!uberDeliverable && doordashDeliverable) {
      if (selectedPlatform !== "doordash") {
        setSelectedPlatform("doordash");
      }
      return;
    }

    if (!uberDeliverable && !doordashDeliverable) {
      setSelectedPlatform(null);
    }
  }, [
    orderType,
    isCustomDeliveryUnlocked,
    uberDeliverable,
    doordashDeliverable,
    selectedPlatform,
  ]);

  useEffect(() => {
    // If pickup, always deliverable
    if (orderType === "pickup") {
      setDeliverable(true);
      setDeliveryFee(0);
      setBaseDeliveryFee(0);
      return;
    }

    if (customRadiusFeeApplicable) {
      const customFee = currentRestaurant?.deliveryAmount || 0;
      setDeliverable(true);
      setBaseDeliveryFee(customFee);
      // For within-radius custom delivery pricing, do not apply delivery tax.
      setDeliveryFee(customFee);
      return;
    }

    // For delivery orders
    if (
      selectedPlatform === null ||
      (uberDeliverable === false && doordashDeliverable === false)
    ) {
      setDeliverable(false);
      setDeliveryFee(0);
      setBaseDeliveryFee(0);
      return;
    }

    setDeliverable(true);
    // Set base delivery fee based on platform
    let baseFee = 0;
    if (selectedPlatform === "uber") {
      baseFee = uberDeliveryFee;
    } else if (selectedPlatform === "doordash") {
      baseFee = doordashDeliveryFee;
    }
    setBaseDeliveryFee(baseFee);

    // Calculate total delivery fee with tax
    if (deliveryTaxes && baseFee > 0) {
      const deliveryTaxAmount = (baseFee * deliveryTaxes) / 100;
      const totalDeliveryFee = baseFee + deliveryTaxAmount;
      setDeliveryFee(totalDeliveryFee);
    } else {
      setDeliveryFee(baseFee);
    }
  }, [
    orderType,
    isCustomDeliveryUnlocked,
    customRadiusFeeApplicable,
    currentRestaurant?.deliveryAmount,
    selectedPlatform,
    uberDeliverable,
    doordashDeliverable,
    uberDeliveryFee,
    doordashDeliveryFee,
    deliveryTaxes,
  ]);

  useEffect(() => {
    if (!orderData) fetchCart();
    console.log("Fetching cart data for user:", user?.id);
  }, [user]);

  const fetchCart = async () => {
    try {
      const userId = user?.id;
      console.log("Fetching cart data for user:", user?.id);

      if (!userId) {
        try {
          const newOrderData = Cookies.get("guest_cart");
          if (newOrderData) {
            const parsedCart = JSON.parse(newOrderData);
            setOrderData((prev) => mergeCartIntoOrder(prev, parsedCart));
          }
        } catch (err) {
          console.error("❌ Failed to parse cart from cookies:", err);
        }
        return;
      }

      const response = await api.get(`/cart/getItem`);
      console.log(`This is my cart-data:`, response.data[0]);

      let mergedCart = response.data[0] || {};

      try {
        // Getting Cart from Cookies for guest users
        const guestCart = Cookies.get("guest_cart");
        if (guestCart) {
          const parsedCart = JSON.parse(guestCart);
          console.log("Merging guest cart items into user cart:", parsedCart);

          const existingItems = response.data[0]?.items || [];
          const guestItems = parsedCart.items || [];

          // Create a map to efficiently merge items by itemId
          const itemsMap = new Map();

          // Add existing items to the map
          existingItems.forEach((item: any) => {
            const key = `${item.itemId}-${JSON.stringify(item.customOptions || [])}`;
            itemsMap.set(key, { ...item });
          });

          // Merge guest items - if item exists, increase quantity; otherwise add new item
          guestItems.forEach((guestItem: any) => {
            const key = `${guestItem.itemId}-${JSON.stringify(guestItem.customOptions || [])}`;

            if (itemsMap.has(key)) {
              // Item exists, increase quantity
              const existingItem = itemsMap.get(key);
              existingItem.quantity += guestItem.quantity;
            } else {
              // New item, add to map
              itemsMap.set(key, { ...guestItem });
            }
          });

          // Convert map back to array
          const mergedItems = Array.from(itemsMap.values());

          mergedCart = {
            ...response.data[0],
            items: mergedItems,
          };

          // Update cart on server with merged data
          await api.post(`/cart`, { cart: mergedCart });

          // Clear guest cart cookie after successful merge
          Cookies.remove("guest_cart");
        }
      } catch (err) {
        console.error("❌ Failed to parse cart from cookies:", err);
      }

      setOrderData((prev) => mergeCartIntoOrder(prev, mergedCart));
    } catch (error) {
      console.error("❌ Failed to sync cart items:", error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white py-3 sm:py-4 ">
        <div className="max-w-7xl  mx-auto px-4 sm:px-6 lg:px-20">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              className="p-2 rounded-full bg-transparent items-center flex justify-center w-[32px] h-[32px] sm:w-[35px] sm:h-[35px] border-[1px] border-black/20 text-black bg-white text-lg font-bold transition dm-button-text"
              onClick={() => navigate(-1)}
            >
              <MoveLeft className="h-4 w-4 dm-back-icon" />
            </button>
            <span className="text-[16px] sm:text-[19px] text-black font-bold">
              SECURE CHECKOUT
            </span>
          </div>
        </div>
      </div>

      <div className="w-full  px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-7xl mx-auto">
        <div className="flex  flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column - Checkout Steps */}
          <div className="w-full lg:w-[65%] space-y-4 sm:space-y-6">
            {user && (
              <AccountSection
                isLoggedIn={user ? true : false}
                setIsModalOpen={() => {
                  setActiveTab("login");
                  setLockAuthToSignup(false);
                  setIsModalOpen(true);
                }}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isLineStyle={true}
              />
            )}
            {orderData?.isAsap && asapPrepEstimate > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4 shrink-0" />
                <span>
                  Your order will be prepared in about{" "}
                  <span className="font-semibold">{asapPrepEstimate} min</span>.
                </span>
              </div>
            )}
            <DeliveryAddressSection
              totalTime={totalTime}
              deliverable={uberDeliverable}
              deliveryStatusLoading={deliveryStatusLoading}
              doordashDeliveryTime={doordashDeliverTime}
              doordashDeliverable={doordashDeliverable}
              uberDeliveryFee={uberDeliveryFee}
              doorDashDeliveryFee={doordashDeliveryFee}
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
              hideDeliveryPartnerOptions={
                orderType === "delivery" && isCustomDeliveryUnlocked
              }
              inlineStyle={true}
              onResetDelivery={handleResetDelivery}
            />

            {itemSubtotalPreTax > 0 && (
              <>
                <Tipmodal
                  total={itemTotal}
                  onTipChange={(amount) => setTipAmount(amount)}
                  ordertype={orderType}
                  defaultTip={currentRestaurant?.defaultTip}
                  deliveryTipToPartner={currentRestaurant?.deliveryTipToPartner}
                  baseTipPercent={currentRestaurant?.baseTipPercent}
                  tipGapPercent={currentRestaurant?.tipGapPercent}
                />
                {!user && <Yourinformation ref={yourinformationRef} />}
  {!user && (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => {
            setActiveTab("signup");
            setLockAuthToSignup(false);
            setIsModalOpen(true);
          }}
          className="w-full bg-black dm-white-btn text-white font-semibold py-3 px-4 rounded-lg text-center transition-colors shadow-sm"
        >
          Signup/Signin to earn rewards
        </button>
        {/* <Yourinformation ref={yourinformationRef} /> */}
      </div>
    )}
                <div className="hidden lg:block">
                <PaymentSection
                    handleOrder={(
                      paymentMethod: string,
                      userId?: string,
                      transactionId?: string,
                    ) => {
                      console.log("🔴 PaymentSection (desktop) handleOrder called:");
                      console.log("  - paymentMethod:", paymentMethod);
                      console.log("  - userId:", userId);
                      console.log("  - transactionId:", transactionId);
                      if (isOdering) return;

                      if (
                        orderType === "delivery" &&
                        !orderData?.location?.address
                      ) {
                        toast.error(
                          "Please select a delivery address before proceeding to payment",
                        );
                        return;
                      }

                      if (!user && yourinformationRef.current) {
                        const isValid = yourinformationRef.current.validate();
                        if (!isValid) return;
                      }

                      setIsOdering(true);
                      const payChoice =
                        paymentMethod === "wallet" ||
                        paymentMethod === "bonus" ||
                        paymentMethod === "bonus_wallet"
                          ? "wallet"
                          : paymentMethod === "stripe" ||
                              paymentMethod === "square" ||
                              paymentMethod === "bonus_square"
                            ? "card"
                            : paymentMethod; // "cash" passes through
                      handlePlaceOrder(
                        payChoice,
                        paymentMethod,
                        userId,
                        transactionId,
                      );
                    }}
                    pricing={toPay}
                    bonusAdjustedPricing={bonusAdjustedToPay}
                    deliverable={deliverable && isGuestInfoComplete}
                    onPaymentMethodChange={setSelectedPaymentMethod}
                    hotelId={currentRestaurant?.id}
                    cashEnabled={currentRestaurant?.cashEnabled}
                  />
                </div>
              </>
            )}
          </div>

          {/* Right Column - Cart & Bill */}
          <div className="w-full lg:w-[35%] space-y-4 sm:space-y-6 lg:pt-0">
            {/* Cart Items */}
            <div className="bg-white border-[1px] border-black/10 rounded-lg p-4 sm:p-5 lg:p-6">
              <h3 className="font-semibold text-[17px] sm:text-[19px] text-black mb-3 text-center">
                Your Order
              </h3>

              <div
                className="space-y-3 pt-3 sm:space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent no-scrollbar"
                style={{
                  maxHeight: "350px",
                  minHeight: "200px",
                }}
              >
                {orderData?.items?.map((item: any, idx: number) => (
                  <CartItemCard
                    key={item.id}
                    item={item}
                    isBonusCovered={itemBonusCoverage[idx] ?? false}
                    tooltipBottom={idx < 1}
                  />
                ))}
                {!orderData?.items?.length && (
                  <p className="text-center text-gray-500 text-sm sm:text-base py-8">
                    Your cart is empty.
                  </p>
                )}
              </div>
            </div>
            <div
              className="space-y-3 sm:space-y-4 overflow-y-auto scrollbar-thin  scrollbar-thumb-gray-300 scrollbar-track-transparent h-full"
            >
              {orderType === "delivery" && withinRadiusForCustomPricing && (
                <div
                  className={`rounded-lg border p-4 ${
                    isCustomDeliveryUnlocked
                      ? "border-green-200 bg-green-50"
                      : "border-amber-200 bg-amber-50"
                  }`}
                >
                  {isCustomDeliveryUnlocked ? (
                    <div className="space-y-1">
                      <p className="text-md font-semibold text-green-900 m-0">
                        Delivery unlocked
                      </p>
                      <p className="text-sm text-green-800">
                        You have unlocked delivery for {currencySymbol}
                        {(currentRestaurant?.deliveryAmount || 0).toFixed(2)}.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-md font-semibold text-main m-0">
                        Unlock flat delivery
                      </p>
                      <p className="text-sm text-main">
                        Add {currencySymbol}{amountLeftToUnlockDelivery.toFixed(2)} more in
                        items to unlock {currencySymbol}
                        {(currentRestaurant?.deliveryAmount || 0).toFixed(2)}{" "}
                        flat delivery.
                      </p>
                    </div>
                  )}
                </div>
              )}
              <BillDetailsCard
                bill={{
                  // Pre-tax item total; item taxes are shown on their own row
                  itemTotal: Number(itemSubtotalPreTax.toFixed(2)),
                  deliveryFee: Number(baseDeliveryFee.toFixed(2)),
                  tipAmount: Number(tipAmount.toFixed(2)),
                  toPay: Number(toPay.toFixed(2)),
                }}
                discountValue={Number(discountValue.toFixed(2))}
                totalDealDiscount={Number((totalDealDiscount || 0).toFixed(2))}
                platform={selectedPlatform}
                orderType={orderType}
                deliveryTax={customRadiusFeeApplicable ? 0 : deliveryTaxes}
                bonusAmountUsed={(selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") ? Number(bonusUsed.toFixed(2)) : 0}
                gstAmount={hasCanadaTax ? gstTotal : undefined}
                pstAmount={hasCanadaTax ? pstTotal : undefined}
                itemTax={
                  (selectedPaymentMethod === "bonus" || selectedPaymentMethod === "bonus_wallet") && bonusUsed > 0
                    ? Number(reducedItemTax.toFixed(2))
                    : // difference of rounded values so item total + taxes adds up exactly
                      Number((Number(itemTotal.toFixed(2)) - Number(itemSubtotalPreTax.toFixed(2))).toFixed(2))
                }
              />
              {user && estimatedLoyaltyBonus > 0 && !paysWithBonus && (
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                  <span>🎁</span>
                  <span>You will earn <strong>{currencySymbol}{estimatedLoyaltyBonus.toFixed(2)}</strong> loyalty bonus on this order</span>
                </div>
              )}
              {user && estimatedLoyaltyBonus === 0 && !paysWithBonus && spendMoreToEarn !== null && spendMoreToEarn > 0 && (
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600">
                  <span>💳</span>
                  <span>Spend <strong>{currencySymbol}{spendMoreToEarn.toFixed(2)}</strong> more to start earning loyalty rewards</span>
                </div>
              )}
            </div>

            {itemSubtotalPreTax > 0 && (
              <div className="block lg:hidden">
                <PaymentSection
                  handleOrder={(
                    paymentMethod: string,
                    userId?: string,
                    transactionId?: string,
                  ) => {
                    console.log("🔴 PaymentSection (mobile) handleOrder called:");
                    console.log("  - paymentMethod:", paymentMethod);
                    console.log("  - userId:", userId);
                    console.log("  - transactionId:", transactionId);
                    if (isOdering) return;

                    if (
                      orderType === "delivery" &&
                      !orderData?.location?.address
                    ) {
                      toast.error(
                        "Please select a delivery address before proceeding to payment",
                      );
                      return;
                    }

                    if (!user && yourinformationRef.current) {
                      const isValid = yourinformationRef.current.validate();
                      if (!isValid) return;
                    }

                    setIsOdering(true);
                    const payChoice =
                      paymentMethod === "wallet" ||
                      paymentMethod === "bonus" ||
                      paymentMethod === "bonus_wallet"
                        ? "wallet"
                        : paymentMethod === "stripe" ||
                            paymentMethod === "square" ||
                            paymentMethod === "bonus_square"
                          ? "card"
                          : paymentMethod; // "cash" passes through
                    handlePlaceOrder(
                      payChoice,
                      paymentMethod,
                      userId,
                      transactionId,
                    );
                  }}
                  pricing={toPay}
                  bonusAdjustedPricing={bonusAdjustedToPay}
                  deliverable={deliverable && isGuestInfoComplete}
                  onPaymentMethodChange={setSelectedPaymentMethod}
                  hotelId={currentRestaurant?.id}
                  cashEnabled={currentRestaurant?.cashEnabled}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <AuthModal
        activeTab={activeTab}
        lockToSignup={lockAuthToSignup}
        onSignupSuccess={() => {
          clearPendingSignupCode();
          clearSignupParamsFromUrl();
          setLockAuthToSignup(false);
        }}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
